import { getApiErrorMessage } from "@gettactic/helpers/src/errors/getApiErrorMessage";
import { getAvailabilityErrorsMessage } from "@/features/reserve/utils/utils-reserve";

/**
 * A readable message for a failed approval response.
 *
 * Approving a request whose type books a reservation creates it synchronously,
 * so the approval can fail the way a booking does: a 409 whose body is
 * `{ availability_errors: [...] }` with no `message`. The generic extractor
 * finds nothing there and falls back to the status text ("Conflict"), so the
 * availability errors are spelled out the same way the reserve flow does.
 */
const OVERRIDE_ERROR_MESSAGES: Record<string, string> = {
  invalid_workflow_override:
    "The reservation details couldn't be applied. Check the dates, room and organizer and try again.",
  overrides_not_allowed:
    "This request's reservation can't be changed at approval time.",
  unknown_workflow:
    "The reservation workflow has changed since this page loaded. Reload and try again.",
  approval_requires_location:
    "This request needs a workspace picked before it can be approved."
};

type AvailabilityError = Parameters<
  typeof getAvailabilityErrorsMessage
>[0][number];

/**
 * The body is whatever the server sent, so each entry is checked for the shape
 * the formatter dereferences before being handed over: a malformed entry would
 * otherwise surface as a crash in the middle of reporting somebody's conflict.
 */
const isAvailabilityError = (entry: unknown): entry is AvailabilityError => {
  if (!entry || typeof entry !== "object") return false;
  const { reason_code, schedule_slot } = entry as {
    reason_code?: unknown;
    schedule_slot?: unknown;
  };
  return (
    typeof reason_code === "string" &&
    !!schedule_slot &&
    typeof schedule_slot === "object"
  );
};

const errorBody = (error: unknown): Record<string, unknown> | null => {
  if (!error || typeof error !== "object") return null;
  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== "object") return null;
  const result = (response as { result?: unknown }).result;
  return result && typeof result === "object"
    ? (result as Record<string, unknown>)
    : null;
};

export const getApprovalErrorMessage = (
  error: unknown,
  dateFormat?: string
): string => {
  const body = errorBody(error);
  const rawAvailabilityErrors = body?.availability_errors;
  const availabilityErrors = Array.isArray(rawAvailabilityErrors)
    ? rawAvailabilityErrors.filter(isAvailabilityError)
    : [];
  if (availabilityErrors.length > 0) {
    return `The reservation couldn't be booked: ${getAvailabilityErrorsMessage(
      availabilityErrors,
      dateFormat
    )}`;
  }
  const code =
    body?.error && typeof body.error === "object"
      ? (body.error as { code?: unknown }).code
      : undefined;
  if (typeof code === "string" && OVERRIDE_ERROR_MESSAGES[code]) {
    return OVERRIDE_ERROR_MESSAGES[code];
  }
  return getApiErrorMessage(error, "Failed to update approval");
};
